// Thin fetch wrappers for the patient intake-form endpoints. The public
// (token-auth) endpoints are intentionally unauthenticated — the UUID token
// in the URL is the only access control.

export const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? 'https://admin.seadent.es/api'
    : 'http://localhost:5000/api';

// ---------- Domain types (mirror server/models/IntakeForm.js) ----------

export type YesNo = 'yes' | 'no' | 'unsure' | null;

export interface IntakePersonal {
    name: string;
    street: string;
    postal_code: string;
    city: string;
    country: string; // ISO 3166-1 alpha-2 code (e.g. 'DE', 'ES', 'GB')
    email: string;
    date_of_birth: string;
    place_of_birth: string;
    phone_private: string;
    phone_work: string;
    occupation: string;
    nie: string;
    doctor_name: string;
    doctor_address: string;
    doctor_phone: string;
}

export interface IntakeHealth {
    cardiovascular: Record<string, YesNo>;
    general: Record<string, YesNo>;
    infectious: Record<string, YesNo>;
    allergies: Record<string, YesNo>;
}

export interface MedicationEntry {
    name: string;
    since: string;
}

export interface SpecialMedicationEntry {
    value: YesNo;
    since: string;
}

export interface MarketingConsentChannel {
    granted: boolean;
    // ISO 8601 — gesetzt vom Server beim Submit, sobald granted=true.
    // Client darf das Feld auch füllen, der Server überschreibt es aber stets.
    granted_at: string | null;
}

export interface MarketingConsent {
    version: string;
    email: MarketingConsentChannel;
    whatsapp: MarketingConsentChannel;
    phone: MarketingConsentChannel;
    // Beide Felder werden serverseitig befüllt — Beweissicherung gemäß AEPD/RGPD.
    ip_at_consent: string | null;
    user_agent_at_consent: string | null;
}

export interface IntakeFormData {
    appointment_id?: number;
    language: string;
    personal: IntakePersonal;
    health: IntakeHealth;
    other_diseases_text: string;
    other_infectious_text: string;
    metals_allergy_text: string;
    pregnancy: { is_pregnant: YesNo; month: string };
    xray: { had_xray: YesNo; when: string };
    medications: MedicationEntry[];
    special_medications: Record<string, SpecialMedicationEntry>;
    major_surgery: { value: YesNo; date: string };
    consent_accepted: boolean;
    signature_data_url: string | null;
    signed_place: string;
    marketing_consent: MarketingConsent;
}

export interface IntakeSchema {
    health_flags: {
        cardiovascular: string[];
        general: string[];
        infectious: string[];
        allergies: string[];
    };
    special_medication_keys: string[];
}

export interface IntakeAppointmentInfo {
    id: number;
    patient_name: string;
    patient_email: string;
    patient_phone: string;
    language: string;
    appointment_date: string;
    start_time: string;
    intake_status: 'pending' | 'submitted';
}

export interface IntakeLoadResponse {
    success: true;
    schema: IntakeSchema;
    appointment: IntakeAppointmentInfo;
    form: IntakeFormData | null;
    readOnly: boolean;
}

export interface IntakeApiError {
    success: false;
    message: string;
}

// ---------- API calls ----------

async function asJson<T>(res: Response): Promise<T> {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const message = (data && (data as IntakeApiError).message) || `HTTP ${res.status}`;
        throw new Error(message);
    }
    return data as T;
}

export async function loadIntake(token: string): Promise<IntakeLoadResponse> {
    const res = await fetch(`${API_BASE_URL}/public/intake/${encodeURIComponent(token)}`);
    return asJson<IntakeLoadResponse>(res);
}

export async function submitIntake(token: string, payload: IntakeFormData): Promise<{ success: true; form_id: number }> {
    const res = await fetch(`${API_BASE_URL}/public/intake/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    return asJson(res);
}

// ---------- Empty form factory (used by the wizard initial state) ----------

export function emptyIntakeForm(language: string): IntakeFormData {
    return {
        language,
        personal: {
            name: '', street: '', postal_code: '', city: '', country: '',
            email: '',
            date_of_birth: '', place_of_birth: '',
            phone_private: '', phone_work: '',
            occupation: '', nie: '',
            doctor_name: '', doctor_address: '', doctor_phone: '',
        },
        health: { cardiovascular: {}, general: {}, infectious: {}, allergies: {} },
        other_diseases_text: '',
        other_infectious_text: '',
        metals_allergy_text: '',
        pregnancy: { is_pregnant: null, month: '' },
        xray: { had_xray: null, when: '' },
        medications: [],
        special_medications: {},
        major_surgery: { value: null, date: '' },
        consent_accepted: false,
        signature_data_url: null,
        signed_place: '',
        marketing_consent: {
            version: 'v1',
            email:    { granted: false, granted_at: null },
            whatsapp: { granted: false, granted_at: null },
            phone:    { granted: false, granted_at: null },
            ip_at_consent: null,
            user_agent_at_consent: null,
        },
    };
}
